import React, { useState } from 'react';
import moment from 'moment';
import EmailAttachment from './EmailAttachment';
import { ChevronDownIcon, ChevronUpIcon, EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { sanitizeEmailHtml } from '../../../../../utils/formatters';

// Patterns that indicate the start of quoted/forwarded content in plain text
const QUOTE_PATTERNS = [
  /^El .+escribi[oó]:\s*$/m,           // Spanish: "El 26 febr. 2026... escribió:"
  /^On .+wrote:\s*$/m,                  // English: "On Feb 26... wrote:"
  /^Le .+a écrit\s*:\s*$/m,            // French
  /^Am .+schrieb .+:\s*$/m,            // German
  /^[-]{2,}\s*(Forwarded|Original|Reenviat)/m, // Forwarded messages
  /^>{1,}/m,                            // Classic ">" quoting
];

/**
 * Split plain text body into new content and quoted content
 */
function splitQuotedText(text) {
  if (!text) return { newContent: text, quotedContent: '' };

  for (const pattern of QUOTE_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      const splitIndex = text.indexOf(match[0]);
      return {
        newContent: text.substring(0, splitIndex).trimEnd(),
        quotedContent: text.substring(splitIndex),
      };
    }
  }
  return { newContent: text, quotedContent: '' };
}

/**
 * Split HTML body into new content and quoted content
 * Gmail wraps quotes in <div class="gmail_quote"> or <blockquote>
 */
function splitQuotedHtml(html) {
  if (!html) return { newHtml: html, quotedHtml: '' };

  // Gmail quote div
  const gmailQuoteIdx = html.indexOf('<div class="gmail_quote"');
  if (gmailQuoteIdx !== -1) {
    return {
      newHtml: html.substring(0, gmailQuoteIdx).trimEnd(),
      quotedHtml: html.substring(gmailQuoteIdx),
    };
  }

  // Standard blockquote
  const blockquoteIdx = html.indexOf('<blockquote');
  if (blockquoteIdx !== -1) {
    return {
      newHtml: html.substring(0, blockquoteIdx).trimEnd(),
      quotedHtml: html.substring(blockquoteIdx),
    };
  }

  return { newHtml: html, quotedHtml: '' };
}

const EmailMessage = ({ interaction, onReply, onUnassign, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showQuoted, setShowQuoted] = useState(false);
  const { details, type, date } = interaction;

  const isOutgoing = type === 'email_sent';
  const isResponse = type === 'email_response';
  const isAutoAssigned = details?.matched_by === 'emailThreadHistory';

  // System emails (automatic) don't have gmail_message_id
  // They are sent via SendGrid, not Gmail API
  const isSystemEmail = isOutgoing && !details?.gmail_message_id;

  // Render minimal version for system emails
  if (isSystemEmail) {
    return (
      <div className='flex items-center justify-end gap-2 py-1'>
        <div className='flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full'>
          <EnvelopeIcon className='w-3.5 h-3.5 text-gray-400' />
          <span className='text-xs text-gray-500'>Email programado enviado</span>
          <span className='text-xs text-gray-400'>{moment(date).format('HH:mm')}</span>
        </div>
      </div>
    );
  }

  // Parse attachments from details
  let attachments = [];
  if (details?.attachments) {
    try {
      attachments = typeof details.attachments === 'string'
        ? JSON.parse(details.attachments)
        : details.attachments;
    } catch {
      attachments = [];
    }
  }

  const hasHtmlBody = details?.body_html && details.body_html.trim() !== '';
  const bodyContent = hasHtmlBody ? details.body_html : details?.body_text || details?.body || '';

  // Sanitize HTML content
  const sanitizedHtml = hasHtmlBody ? sanitizeEmailHtml(bodyContent) : null;

  // Get preview text (first 150 chars of plain text, decode HTML entities)
  const decodeEntities = text => {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  };
  const previewText = details?.body_text?.substring(0, 150) ||
    (bodyContent ? decodeEntities(bodyContent.replace(/<[^>]*>/g, '')).substring(0, 150) : '');

  return (
    <div
      className={`rounded-lg border ${
        isOutgoing
          ? 'bg-blue-50 border-blue-200'
          : 'bg-white border-gray-200'
      }`}
    >
      {/* Email Header */}
      <div
        className='p-3 cursor-pointer hover:bg-opacity-80 transition-colors'
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='flex-1 min-w-0'>
            {/* From / To */}
            <div className='flex items-center gap-2 text-sm'>
              <span className='font-medium text-gray-700'>
                {isOutgoing ? 'Para:' : 'De:'}
              </span>
              <span className='text-gray-600 truncate'>
                {isOutgoing
                  ? (details?.to || details?.recipient_email || 'Deudor')
                  : (details?.from_name || details?.from || 'Deudor')}
              </span>
            </div>

            {/* Subject */}
            <p className='font-semibold text-gray-800 mt-1 truncate'>
              {details?.subject || '(Sin asunto)'}
            </p>

            {/* Auto-assigned badge */}
            {isAutoAssigned && (
              <div className='flex items-center gap-1 mt-1'>
                <ExclamationTriangleIcon className='w-3.5 h-3.5 text-amber-500' />
                <span className='text-xs text-amber-600 font-medium'>Asignado automáticamente</span>
              </div>
            )}

            {/* Preview when collapsed */}
            {!isExpanded && previewText && (
              <p className='text-sm text-gray-500 mt-1 truncate'>
                {previewText}...
              </p>
            )}
          </div>

          {/* Date and expand/collapse */}
          <div className='flex flex-col items-end gap-1 flex-shrink-0'>
            <span className='text-xs text-gray-500'>
              {moment(date).format('DD/MM/YY HH:mm')}
            </span>
            {isExpanded ? (
              <ChevronUpIcon className='w-4 h-4 text-gray-400' />
            ) : (
              <ChevronDownIcon className='w-4 h-4 text-gray-400' />
            )}
          </div>
        </div>
      </div>

      {/* Email Body - Expanded */}
      {isExpanded && (
        <div className='border-t border-gray-200'>
          {/* Body Content */}
          <div className='p-3'>
            {sanitizedHtml ? (() => {
              const { newHtml, quotedHtml } = splitQuotedHtml(sanitizedHtml);
              return (
                <>
                  <div
                    className='prose prose-sm max-w-none text-gray-700 [&_a]:text-blue-600 [&_a]:underline'
                    dangerouslySetInnerHTML={{ __html: newHtml }}
                  />
                  {quotedHtml && (
                    <>
                      <button
                        type='button'
                        onClick={e => { e.stopPropagation(); setShowQuoted(!showQuoted); }}
                        className='mt-2 px-2 py-0.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
                      >
                        ···
                      </button>
                      {showQuoted && (
                        <div
                          className='mt-2 pl-3 border-l-2 border-gray-200 prose prose-sm max-w-none text-gray-400 [&_a]:text-blue-400 [&_a]:underline'
                          dangerouslySetInnerHTML={{ __html: quotedHtml }}
                        />
                      )}
                    </>
                  )}
                </>
              );
            })() : (() => {
              const { newContent, quotedContent } = splitQuotedText(bodyContent);
              return (
                <>
                  <p className='text-sm text-gray-700 whitespace-pre-wrap'>
                    {newContent || '(Sin contenido)'}
                  </p>
                  {quotedContent && (
                    <>
                      <button
                        type='button'
                        onClick={e => { e.stopPropagation(); setShowQuoted(!showQuoted); }}
                        className='mt-2 px-2 py-0.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
                      >
                        ···
                      </button>
                      {showQuoted && (
                        <p className='mt-2 pl-3 border-l-2 border-gray-200 text-sm text-gray-400 whitespace-pre-wrap'>
                          {quotedContent}
                        </p>
                      )}
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className='px-3 pb-3'>
              <p className='text-xs font-medium text-gray-500 mb-2'>
                Adjuntos ({attachments.length})
              </p>
              <div className='space-y-2'>
                {attachments.map((attachment, index) => (
                  <EmailAttachment
                    key={`${attachment.filename}-${index}`}
                    attachment={attachment}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Actions for incoming emails */}
          {isResponse && (onReply || isAutoAssigned) && (
            <div className='px-3 pb-3 border-t border-gray-100 pt-2 flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {onReply && (
                  <button
                    onClick={() => onReply(details?.gmail_thread_id, details?.subject)}
                    className='text-sm text-blue-600 hover:text-blue-700 font-medium'
                  >
                    Responder
                  </button>
                )}
              </div>
              {isAutoAssigned && onUnassign && (
                <button
                  onClick={() => onUnassign(interaction)}
                  className='text-xs text-amber-600 hover:text-amber-700 font-medium'
                >
                  Desasignar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailMessage;
